import type { AttachmentKey, StudioScript } from "./AiChatSidebar";
import { AttachmentChip, AttachmentIndicator } from "./AttachmentChip";
import {
  AttachmentBar,
  AttachmentLabel,
  truncateName,
} from "./attachmentChipStyles";
import { SeeingChips } from "./SeeingChips";

// The bar under the composer that says what goes with the next message.
// Two modes: "attach" (the old Studio — pick what to send, reset per
// message) and "follows-view" (the new Studio — everything on screen is
// sent, a click hides an item, sticky for the conversation).

// ---------------------------------------------------------------------------
// Attachment chips bar
// ---------------------------------------------------------------------------

export interface AttachmentChipsProps {
  scripts: StudioScript[];
  hasInput: boolean;
  hasSchema: boolean;
  hasTests: boolean;
  testFailures: number;
  hasPipeline: boolean;
  /** The pipeline code does not parse: it is sent as text with its parse
   *  error rather than as the structured pipeline. */
  pipelineUnparsed?: boolean;
  hasError: boolean;
  attachments: Set<AttachmentKey>;
  onToggle: (key: AttachmentKey) => void;
  /** Items the engine will cut at its line cap. */
  truncated?: Set<AttachmentKey>;
}

function testChipTitle(testFailures: number, hasTests: boolean): string {
  if (testFailures > 0) {
    const s = testFailures === 1 ? "" : "s";
    return `${testFailures} test${s} failed — results will be shared`;
  }
  return hasTests ? "Attach test cases" : "No test cases";
}

function SimpleChip({
  attachmentKey,
  available,
  selected,
  onToggle,
  title,
  label,
}: {
  attachmentKey: AttachmentKey;
  available: boolean;
  selected: boolean;
  onToggle: (key: AttachmentKey) => void;
  title: string;
  label: string;
}) {
  return (
    <AttachmentChip
      label={label}
      selected={selected}
      available={available}
      onClick={() => available && onToggle(attachmentKey)}
      title={title}
    />
  );
}

function AttachmentChips({
  scripts,
  hasInput,
  hasSchema,
  hasTests,
  testFailures,
  hasPipeline,
  hasError,
  attachments,
  onToggle,
}: AttachmentChipsProps) {
  const nonEmptyScripts = scripts.filter((s) => s.content.trim());
  const testsSelected = attachments.has("tests");
  const hasFailures = testFailures > 0;

  return (
    <AttachmentBar>
      <AttachmentLabel>Attach</AttachmentLabel>

      <SimpleChip
        attachmentKey="input"
        available={hasInput}
        selected={attachments.has("input")}
        onToggle={onToggle}
        title={hasInput ? "Attach input document" : "No input document"}
        label="Input"
      />

      {nonEmptyScripts.map((s) => {
        const key: AttachmentKey = `script:${s.name}`;
        return (
          <SimpleChip
            key={s.name}
            attachmentKey={key}
            available={true}
            selected={attachments.has(key)}
            onToggle={onToggle}
            title={`Attach ${s.name}`}
            label={truncateName(s.name)}
          />
        );
      })}

      <SimpleChip
        attachmentKey="schema"
        available={hasSchema}
        selected={attachments.has("schema")}
        onToggle={onToggle}
        title={hasSchema ? "Attach schema" : "No schema"}
        label="Schema"
      />

      <AttachmentChip
        label={hasFailures ? `Tests (${testFailures} failed)` : "Tests"}
        selected={testsSelected}
        available={hasTests}
        error={hasFailures && testsSelected}
        onClick={() => hasTests && onToggle("tests")}
        title={testChipTitle(testFailures, hasTests)}
      />

      <SimpleChip
        attachmentKey="pipeline"
        available={hasPipeline}
        selected={attachments.has("pipeline")}
        onToggle={onToggle}
        title={hasPipeline ? "Attach pipeline" : "No pipeline"}
        label="Pipeline"
      />

      {hasError && (
        <AttachmentIndicator
          label="Error"
          title="Error will be sent automatically"
        />
      )}
    </AttachmentBar>
  );
}

export function ChipBar({
  mode,
  ...p
}: AttachmentChipsProps & { mode: "attach" | "follows-view" }) {
  return mode === "follows-view" ? (
    <SeeingChips {...p} />
  ) : (
    <AttachmentChips {...p} />
  );
}
